import { create } from 'zustand';
import { ChessAdapter } from '@strategy4kids/game-engine';
import type { CoachAdviceItem } from '@strategy4kids/ai-coach';
import type { GameEvent, GameSnapshot, LiveClock, LiveEvent, MoveRecord } from '@strategy4kids/protocol';
import { ApiError, api, type CreateGameInput } from '../services/api.js';
import { connectLive, type LiveClient } from '../services/live.js';
import { subscribeGame } from '../services/sse.js';
import type { SquareTarget } from './game-store.js';

export type OnlinePhase = 'idle' | 'connecting' | 'playing' | 'gameover';

export type OnlineOpenInput =
  | { readonly kind: 'create'; readonly token: string; readonly input: CreateGameInput }
  | { readonly kind: 'join'; readonly token: string; readonly gameId: string }
  | { readonly kind: 'resume'; readonly token: string; readonly gameId: string };

export type LiveStatus = 'idle' | 'connected' | 'reconnecting' | 'closed';

interface OnlineStore {
  readonly phase: OnlinePhase;
  readonly game: GameSnapshot | null;
  readonly clock: LiveClock | null;
  readonly moves: MoveRecord[];
  readonly opponentOnline: boolean;
  readonly liveStatus: LiveStatus;
  readonly selected: string | null;
  readonly targets: SquareTarget[];
  readonly lastMove: { from: string; to: string } | null;
  readonly error: string | null;
  readonly coach: CoachAdviceItem[] | null;
  readonly coachLoading: boolean;
  /** True after a coach tap in a rated game (coach is off). */
  readonly coachOffNote: boolean;
  readonly submitPending: boolean;

  open(input: OnlineOpenInput): Promise<void>;
  tap(square: string): void;
  resign(): Promise<void>;
  askCoach(): Promise<void>;
  refresh(): Promise<void>;
  close(): void;
}

const adapter = new ChessAdapter();

// Module-level singletons — one online session at a time.
let currentToken: string | null = null;
let unsubscribe: (() => void) | null = null;
let liveClient: LiveClient | null = null;
/** Bumped by close() so an in-flight open() can bail after a late fetch. */
let session = 0;

export const useOnlineStore = create<OnlineStore>()((set, get) => {
  async function applyGame(game: GameSnapshot): Promise<void> {
    set({
      game,
      phase: game.status === 'completed' ? 'gameover' : 'playing',
      selected: null,
      targets: [],
      lastMove: null,
      error: null,
      submitPending: false,
      coachLoading: false,
      coachOffNote: false,
    });
  }

  async function fetchAndApply(): Promise<void> {
    const { game } = get();
    if (!game) return;
    try {
      if (game.opponent === 'human') {
        const snap = await api.fetchLiveGame(currentToken ?? '', game.id);
        if (get().game?.id !== game.id) return;
        await applyGame(snap.game);
        set({ clock: snap.clock });
      } else {
        const updated = await api.fetchGame(currentToken ?? '', game.id);
        if (get().game?.id !== game.id) return;
        await applyGame(updated);
      }
    } catch {
      // keep current state — a later live event or manual refresh retries
    }
  }

  async function submit(from: string, to: string): Promise<void> {
    const { game } = get();
    if (!game) return;
    const guard = game.id;
    if (game.opponent === 'human' && liveClient) {
      set({ error: null, selected: null, targets: [] });
      liveClient.sendMove(`${from}${to}`);
      return;
    }
    set({ submitPending: true, error: null, selected: null, targets: [] });
    try {
      const updated = await api.submitMove(currentToken ?? '', game.id, `${from}${to}`);
      if (get().game?.id !== guard) return;
      await applyGame(updated);
    } catch (err) {
      if (get().game?.id !== guard) return;
      if (err instanceof ApiError && err.status === 409) {
        await fetchAndApply();
      } else {
        set({ error: err instanceof Error ? err.message : 'Could not make that move.' });
      }
    } finally {
      if (get().game?.id === guard) set({ submitPending: false });
    }
  }

  function subscribe(token: string, gameId: string, live: boolean): void {
    unsubscribe?.();
    unsubscribe = null;
    liveClient?.close();
    liveClient = null;
    if (live) {
      liveClient = connectLive(gameId, {
        token,
        onEvent: (event) => void handleLiveEvent(event).catch(() => {}),
        onStatus: (status) => {
          if (get().game?.id === gameId) set({ liveStatus: status });
        },
      });
      unsubscribe = () => {
        liveClient?.close();
        liveClient = null;
      };
    } else {
      unsubscribe = subscribeGame(gameId, {
        token,
        onEvent: (event) => void handleEvent(event).catch(() => {}),
      });
    }
  }

  async function handleLiveEvent(event: LiveEvent): Promise<void> {
    const game = get().game;
    if (!game || game.id !== event.gameId) return;
    switch (event.type) {
      case 'live:snapshot': {
        set({ clock: event.clock, moves: [], coach: null });
        await applyGame(event.game);
        return;
      }
      case 'live:move': {
        set({
          game: { ...game, fen: event.fen, turn: event.by === 'w' ? 'b' : 'w' },
          clock: event.clock,
          lastMove: { from: event.move.uci.slice(0, 2), to: event.move.uci.slice(2, 4) },
          moves: [...get().moves, event.move].slice(-20),
          selected: null,
          targets: [],
          coach: null,
        });
        return;
      }
      case 'live:clock':
        set({ clock: event.clock });
        return;
      case 'live:join':
        if (event.side !== game.side) set({ opponentOnline: true });
        return;
      case 'live:leave':
        if (event.side !== game.side) set({ opponentOnline: false });
        return;
      case 'live:end':
        await fetchAndApply();
        return;
      case 'live:error':
        set({ error: event.message });
        return;
    }
  }

  async function handleEvent(event: GameEvent): Promise<void> {
    if (event.type === 'game:update') {
      const game = get().game;
      if (!game || game.id !== event.gameId) return;
      await applyGame(event.snapshot);
      return;
    }
    if (event.type === 'game:move') {
      const game = get().game;
      if (!game || game.id !== event.gameId) return;
      set({
        game: { ...game, fen: event.fen, turn: event.turn },
        lastMove: { from: event.move.uci.slice(0, 2), to: event.move.uci.slice(2, 4) },
        selected: null,
        targets: [],
        coach: null,
      });
      return;
    }
    if (event.type === 'game:ended') {
      await fetchAndApply();
    }
  }

  return {
    phase: 'idle',
    game: null,
    clock: null,
    moves: [],
    opponentOnline: false,
    liveStatus: 'idle',
    selected: null,
    targets: [],
    lastMove: null,
    error: null,
    coach: null,
    coachLoading: false,
    coachOffNote: false,
    submitPending: false,

    async open(input) {
      const mySession = session;
      // Joining an already-started game no longer dead-ends: the server's
      // spectate fallback returns the read-only snapshot (spectating: true),
      // and the screens hide play actions for it. Chess spectators get the
      // board + live feed with input refused server-side; backgammon's
      // spectator mode hides its controls outright.
      const game =
        input.kind === 'create'
          ? await api.createGame(input.token, input.input)
          : input.kind === 'join'
            ? await api
                .joinGame(input.token, input.gameId)
                .catch((err: unknown) =>
                  err instanceof ApiError && err.status === 409
                    ? api.spectateGame(input.token, input.gameId)
                    : Promise.reject(err),
                )
            : await api.fetchGame(input.token, input.gameId);
      if (mySession !== session) return;
      currentToken = input.token;
      set({ clock: null, moves: [], opponentOnline: false, liveStatus: 'idle' });
      await applyGame(game);
      subscribe(input.token, game.id, game.opponent === 'human');
    },

    tap(square) {
      const { game, selected, targets, phase, submitPending } = get();
      if (phase !== 'playing' || !game || game.status !== 'active' || submitPending) return;
      if (game.turn !== game.side) return;
      const pos = adapter.fromFen(game.fen);
      const piece = adapter.pieceAt(pos, square);
      if (selected === null) {
        if (piece && piece.color === pos.turn) {
          set({ selected: square, targets: adapter.legalMovesForSquare(pos, square).map((t) => ({ square: t.to, capture: t.capture })) });
        }
        return;
      }
      if (square === selected) {
        set({ selected: null, targets: [] });
        return;
      }
      if (targets.some((t) => t.square === square)) {
        void submit(selected, square);
        return;
      }
      if (piece && piece.color === pos.turn) {
        set({ selected: square, targets: adapter.legalMovesForSquare(pos, square).map((t) => ({ square: t.to, capture: t.capture })) });
      }
    },

    async resign() {
      const { game, phase } = get();
      if (!game || phase === 'gameover') return;
      const guard = game.id;
      set({ submitPending: true, error: null });
      try {
        const updated = await api.resignGame(currentToken ?? '', game.id);
        await applyGame(updated);
      } catch (err) {
        set({ error: err instanceof Error ? err.message : 'Could not resign.' });
      } finally {
        if (get().game?.id === guard) set({ submitPending: false });
      }
    },

    async askCoach() {
      const { game, phase, coachLoading } = get();
      if (!game || phase === 'gameover' || coachLoading) return;
      if (game.rated) {
        set({ coachOffNote: true });
        return;
      }
      set({ coachLoading: true, coach: null, error: null });
      try {
        const resp = await api.fetchCoachAdvice(currentToken ?? '', game.id);
        if (get().game?.id !== game.id) return;
        set({ coach: resp.moves });
      } catch (err) {
        if (get().game?.id === game.id) {
          set({ error: err instanceof Error ? err.message : 'The coach is thinking too hard.' });
        }
      } finally {
        if (get().game?.id === game.id) set({ coachLoading: false });
      }
    },

    async refresh() {
      await fetchAndApply();
    },

    close() {
      session += 1;
      unsubscribe?.();
      unsubscribe = null;
      liveClient?.close();
      liveClient = null;
      currentToken = null;
      set({
        phase: 'idle',
        game: null,
        clock: null,
        moves: [],
        opponentOnline: false,
        liveStatus: 'idle',
        selected: null,
        targets: [],
        lastMove: null,
        error: null,
        coach: null,
        coachLoading: false,
        coachOffNote: false,
        submitPending: false,
      });
    },
  };
});
